import { getLogger } from '../../utils/SearchLogger';

export class ResultSource {
	static PAGE_LOAD_THRESHOLD: number;
	searchSource: Searchable;
	query: SearchablesQuery;
	startIndex: number;
	limit: number;
	results: SearchResult;
	resultItems: Array<ResultItem>;
	pagesAvailable: number;
	pagesLoaded: number;
	nextItemIndex: number;
	fetchingPage: Promise<void> | void;
	logger: Logger;
	flags: Array<string>;

	constructor(
		searchSource: Searchable,
		query: SearchablesQuery,
		startIndex: number,
		limit: number,
		results: SearchResult,
		userAccountType: IMSAccountType,
		flags?: Array<string>,
	) {
		// the SearchResult object
		this.results = results;
		// reference to our searchable source
		this.searchSource = searchSource;
		// query, filters and sort that was used on the initial search
		this.query = query;
		this.flags = flags;
		this.logger = getLogger();

		// FIXME: this is hacky
		// account for an enterprise user that doesn't have access to Lightroom
		if (searchSource.name.startsWith('USS')) {
			// $FlowFixMe - add getSource() to Searchable
			const source = searchSource.getSource && searchSource.getSource();

			if (
				source.includes('Lightroom') &&
				this.results &&
				this.results.serverResult &&
				this.results.serverResult.status === 403
			) {
				// check if the user is an enterprise user
				if (
					(userAccountType && userAccountType === 'type2') ||
					userAccountType === 'type2e' ||
					userAccountType === 'type3'
				) {
					// they are an enterprise user, fake that there are zero results
					// fake a 200 OK
					this.results.serverResult.status = 200;
					this.results = {
						duration: results.duration,
						resultItems: [],
						serverResult: this.results.serverResult,
						totalResults: 0,
					};
				}
			}
		}

		// FIXME: We should be rejecting instead of getting Errors
		if (!(this.results instanceof Error)) {
			// startIndex for initial search
			this.startIndex = startIndex;
			// limit aka pagesize
			this.limit = limit;
			// initial set of result items
			this.resultItems = this.results.resultItems;
			// find out how many pages are in the results
			this.pagesAvailable =
				Math.floor(this.results.totalResults / limit) + (this.results.totalResults % limit > 0 ? 1 : 0);
			this.pagesLoaded = 1;
			// keep an index into our overall results array
			this.nextItemIndex = 0;
			this.fetchingPage = undefined;
		}
	}

	getTotalResults(): number {
		return this.results.totalResults;
	}

	getSearchableName(): string {
		return this.searchSource.name;
	}

	getDuration(): number {
		return this.results.duration;
	}

	async offerResultItem(): Promise<ResultItem | typeof undefined> {
		let resultItem: ResultItem | undefined;

		// make sure we are in the bounds of the resultItems
		// or if we are not within bounds, check to see if we are fetching a page
		if ((this.resultItems && this.nextItemIndex < this.resultItems.length) || this.fetchingPage) {
			if (this.fetchingPage) {
				await this.fetchingPage;
			}

			resultItem = this.resultItems[this.nextItemIndex];
		}

		return resultItem;
	}

	offerAccepted(): void {
		++this.nextItemIndex;

		if (
			this.isThresholdExceeded(this.nextItemIndex, this.startIndex, this.limit) &&
			this.pagesLoaded < this.pagesAvailable &&
			!this.fetchingPage
		) {
			this.logger.log('Search Manager', `${this.getSearchableName()}: threshold exceeded, fetching page`);
			this.fetchingPage = this.fetchNextPage();
			this.fetchingPage.then(() => {
				this.fetchingPage = undefined;
			});
		}
	}

	isThresholdExceeded(nextItemIndex: number, startIndex: number, limit: number): boolean {
		// TODO: make a strategy pattern so we can test different types of thresholding algorithms
		// normalize startIndex
		return (nextItemIndex - startIndex) / limit > ResultSource.PAGE_LOAD_THRESHOLD;
	}

	async fetchNextPage(): Promise<void> {
		this.logger.log(
			'Search Manager',
			`${this.getSearchableName()}: fetchNexPage, pagesLoaded=${this.pagesLoaded}, pagesAvailable=${
				this.pagesAvailable
			}, pre-count = ${this.resultItems.length}`,
		);

		if (this.pagesLoaded < this.pagesAvailable) {
			try {
				const startIndex = this.results.nextPageReference
					? this.results.nextPageReference
					: this.startIndex + this.limit;
				// pass new starting index and limit to search
				const newResults = await this.searchSource.search(this.query, startIndex, this.limit, this.flags);

				// FIXME: we should not be returning SearchErrors here, reject instead
				if (!(newResults instanceof Error)) {
					// create a new array with the next page of resultItems added to it
					this.resultItems = this.resultItems.concat(newResults.resultItems);

					// update the next page reference if one exists
					if (newResults.nextPageReference) {
						this.results.nextPageReference = newResults.nextPageReference;
					}

					// update the startIndex since the call completed successfully
					this.startIndex += this.limit;
					// bump the number of pages loaded
					++this.pagesLoaded;
				}
			} catch (err) {
				// do nothing here, if another item is accepted fetchNextPage will be called
			}
		}
	}
}
// static properties
ResultSource.PAGE_LOAD_THRESHOLD = 0.25;
